<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="60vh"
      scrollable
    >
      <v-card>
        <v-card-title>
          Benefit Compensation Types
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-data-table
            dense
            fixed-header
            height="50vh"
            :headers="tableHeaders"
            :items="compensationTypes"
            :items-per-page="-1"
            hide-default-footer
          >
            <template v-slot:[`item.Name`]="{ item }">
              <v-text-field
                :rules="[rules.required, rules.alreadyExist]"
                v-if="editMode && currentItem.Type_ID === item.Type_ID"
                v-model="currentItem.Name"
              ></v-text-field>
              <span v-else>{{ item.Name }}</span>
            </template>
            <template v-slot:[`item.Limit`]="{ item }">
              <v-text-field
                v-if="editMode && currentItem.Type_ID === item.Type_ID"
                v-model="currentItem.Limit"
                :rules="[rules.positiveNumber]"
                type="number"
              ></v-text-field>
              <span v-else>{{ item.Limit }} {{ item.Limit ? '$' : ''}}</span>
            </template>
            <template v-slot:[`item.Actions`]="{ item }">
              <v-btn
                :disabled="(editMode && currentItem.Type_ID === item.Type_ID && canSave) || (editMode && currentItem.Type_ID !== item.Type_ID)"
                :color="editMode && currentItem.Type_ID === item.Type_ID ? 'success' : 'primary'"
                small
                icon
                @click="onEditClick(item)"
              >
                <v-icon>
                  {{ currentItem?.Type_ID === item.Type_ID && !deleteMode ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                small
                icon
                color="red darken-2"
                v-if="item.Type_ID !== currentItem?.Type_ID || deleteMode"
                :disabled="editMode || !item.Can_Delete"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
              <v-btn
                v-if="editMode && currentItem.Type_ID === item.Type_ID"
                color="primary"
                small
                icon
                @click="onCancelEditClick"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            :disabled="editMode"
            @click="onCreateClick"
          >
            Create New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      max-width="500"
      @click:outside="onCancelCreateNew"
      v-model="createNewTypeDialog"
      v-if="createNewTypeDialog"
    >
      <v-card>
        <v-card-title>
          New Compensation Type
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="onCancelCreateNew"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6" lg="6">
              <v-text-field
                v-model="currentItem.Name"
                label="Type Name"
                autofocus
                :rules="[rules.required, rules.alreadyExist]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" lg="6">
              <v-text-field
                v-model="currentItem.Limit"
                label="Limit"
                type="number"
                :rules="[rules.positiveNumber]"
              >
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="canSave"
            @click="createNewCompensationType"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="deleteTypeDialog"
      v-model="deleteTypeDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteCompensationType"
      @onCancelClick="cancelDelete"
      @onCloseClick="cancelDelete"
    />
  </div>
</template>
<script>
export default {
  name: 'CompensationTypesManager',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      compensationTypes: [],
      createNewTypeDialog: false,
      currentItem: null,
      deleteTypeDialog: false,
      editMode: false,
      deleteMode: false,
      tableHeaders: [
        {
          text: 'Type Name',
          value: 'Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Limit',
          value: 'Limit',
          divider: true,
          width: 100,
          align: 'center',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          value: 'Actions',
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: 100,
          sortable: false
        }
      ],
      rules: {
        required: v => !!v || 'Required',
        alreadyExist: () => !this.validateOnAlreadyExist || 'The type name already exists',
        positiveNumber: v => { return v >= 0 || 'Positive number required' }
      }
    }
  },
  mounted () {
    this.getCompensationTypes()
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    canSave () {
      return !this.currentItem.Name || this.validateOnAlreadyExist || !this.currentItem.Name.trim().length || +this.currentItem.Limit < 0
    },
    validateOnAlreadyExist () {
      return this.compensationTypes.some(el => {
        if (this.editMode) {
          if (el.Type_ID === this.currentItem.Type_ID) {
            return el.Name === this.currentItem.Name.trim() && +el.Limit === +this.currentItem.Limit
          } else {
            return el.Name === this.currentItem.Name.trim()
          }
        } else if (!this.editMode) {
          return (el.Name === this.currentItem.Name.trim())
        }
      })
    }
  },
  methods: {
    onCreateClick () {
      this.currentItem = {
        Type_ID: -1,
        Name: '',
        Limit: null
      }
      this.createNewTypeDialog = true
    },
    onCancelCreateNew () {
      this.currentItem = null
      this.createNewTypeDialog = false
    },
    onEditClick (item) {
      if (this.editMode) {
        this.updateCompensationType()
      } else {
        this.tableHeaders[0].sortable = false
        this.currentItem = Object.assign({}, item)
      }
      this.editMode = !this.editMode
    },
    onCancelEditClick () {
      this.editMode = false
      this.currentItem = null
      this.tableHeaders[0].sortable = true
    },
    onDeleteClick (item) {
      this.deleteMode = true
      this.deleteTypeDialog = true
      this.currentItem = Object.assign({}, item)
    },
    cancelDelete () {
      this.deleteMode = false
      this.deleteTypeDialog = false
      this.currentItem = null
    },
    getCompensationTypes () {
      this.axios.get('/api/expenditure_compensation_types_to_manage', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.compensationTypes = res.data
      }).catch(err => {
        this.$store.dispatch('setError', { errorMsg: err.message + ' - ' + err.response.data })
      })
    },
    createNewCompensationType () {
      this.axios.post('/api/expenditure_compensation_types', {
        TypeName: this.currentItem.Name.trim(),
        Limit: +this.currentItem.Limit
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.onCancelCreateNew()
        this.getCompensationTypes()
        this.$store.dispatch('setSuccess', { successMsg: 'New expenditure compensation type successfully created' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    updateCompensationType () {
      this.axios.put(`/api/expenditure_compensation_types/${this.currentItem.Type_ID}`, {
        TypeName: this.currentItem.Name.trim(),
        Limit: +this.currentItem.Limit
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getCompensationTypes()
        this.onCancelEditClick()
        this.$store.dispatch('setSuccess', { successMsg: 'Type successfully updated' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    deleteCompensationType () {
      this.axios.delete(`/api/expenditure_compensation_types/${this.currentItem.Type_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getCompensationTypes()
        this.cancelDelete()
        this.$store.dispatch('setSuccess', { successMsg: 'Type successfully deleted' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    }
  }
}
</script>
